/**
 * GitControlBar — inline git status + actions for workbench-mode chats.
 *
 * Renders below the workspace selector when at least one repo is anchored.
 * For each repo shows:
 *   - Branch name
 *   - Clean/dirty status (# modified/staged files)
 *   - Quick actions: Stage All · Commit · Push
 *
 * Actions call backend endpoints that shell out to git.
 * All operations are per-repo, visually grouped.
 */

import { useState, useEffect, useCallback } from "react";
import {
  GitBranch,
  GitCommit,
  Upload,
  Download,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

export interface RepoGitStatus {
  name: string;
  path: string;
  branch: string;
  isDirty: boolean;
  staged: number;
  modified: number;
  untracked: number;
  ahead: number;
  behind: number;
  error?: string | null;
}

interface GitControlBarProps {
  workspacePaths: string[];
  disabled?: boolean;
  onStatusChange?: () => void;
}

export function GitControlBar({
  workspacePaths,
  disabled,
  onStatusChange,
}: GitControlBarProps) {
  const [repos, setRepos] = useState<RepoGitStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  // Action states per repo (keyed by path)
  const [actionLoading, setActionLoading] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<Record<string, string>>({});
  const [commitMsg, setCommitMsg] = useState<Record<string, string>>({});
  const [showCommitInput, setShowCommitInput] = useState<Record<string, boolean>>({});

  const fetchStatus = useCallback(async () => {
    if (workspacePaths.length === 0) {
      setRepos([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/git/status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paths: workspacePaths }),
      });
      const data = await res.json();
      if (Array.isArray(data.repos)) {
        setRepos(data.repos);
      }
    } catch (err) {
      console.error("Failed to fetch git status:", err);
    } finally {
      setLoading(false);
    }
  }, [workspacePaths]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const runAction = async (
    path: string,
    action: "stage" | "commit" | "push" | "fetch" | "pull",
    message?: string,
  ) => {
    setActionLoading((p) => ({ ...p, [path]: action }));
    setActionError((p) => ({ ...p, [path]: "" }));
    try {
      const res = await fetch("/api/git/action", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path, action, message }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setActionError((p) => ({
          ...p,
          [path]: data.error ?? `HTTP ${res.status}`,
        }));
        return;
      }
      // Success: refresh status + hide commit input
      setShowCommitInput((p) => ({ ...p, [path]: false }));
      setCommitMsg((p) => ({ ...p, [path]: "" }));
      await fetchStatus();
      onStatusChange?.();
    } catch (err) {
      setActionError((p) => ({
        ...p,
        [path]: err instanceof Error ? err.message : "Network error",
      }));
    } finally {
      setActionLoading((p) => ({ ...p, [path]: "" }));
    }
  };

  if (workspacePaths.length === 0) return null;

  return (
    <div className="mx-4 mt-2 border border-[var(--bw-border)] bg-[var(--bw-bg-alt)] font-mono text-xs">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[var(--bw-bg)] transition-colors"
      >
        {expanded ? (
          <ChevronDown size={12} className="text-[var(--bw-muted)]" />
        ) : (
          <ChevronRight size={12} className="text-[var(--bw-muted)]" />
        )}
        <GitBranch size={12} className="text-[var(--bw-accent)]" />
        <span className="text-[10px] uppercase tracking-wider text-[var(--bw-muted)]">
          Git
        </span>
        <span className="flex-1" />
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            fetchStatus();
          }}
          disabled={loading || disabled}
          className="p-1 hover:text-[var(--bw-accent)] disabled:opacity-50"
          title="Refresh git status"
        >
          <RefreshCw
            size={10}
            className={loading ? "animate-spin" : ""}
          />
        </button>
      </button>

      {/* Repo rows */}
      {expanded && (
        <div className="border-t border-[var(--bw-border)]">
          {repos.length === 0 && !loading && (
            <div className="px-3 py-2 text-[var(--bw-muted)]">
              No repos to show.
            </div>
          )}

          {repos.map((repo) => {
            const isLoading = !!actionLoading[repo.path];
            const currentAction = actionLoading[repo.path];
            const error = actionError[repo.path];
            const showInput = showCommitInput[repo.path];
            const msg = commitMsg[repo.path] ?? "";

            return (
              <div
                key={repo.path}
                className="px-3 py-2 border-t border-[var(--bw-border)] first:border-t-0"
              >
                {/* Row 1: name, branch, status */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[var(--bw-fg)] font-medium">
                    {repo.name}
                  </span>
                  <span className="text-[var(--bw-muted)] flex items-center gap-1">
                    <GitBranch size={10} />
                    {repo.branch}
                  </span>
                  {repo.isDirty ? (
                    <span className="text-[var(--bw-warn,#f77)] flex items-center gap-1">
                      <AlertCircle size={10} />
                      {repo.modified > 0 && `${repo.modified}M`}
                      {repo.untracked > 0 && ` ${repo.untracked}U`}
                      {repo.staged > 0 && ` ${repo.staged}S`}
                    </span>
                  ) : (
                    <span className="text-[var(--bw-ok,#7fe)] flex items-center gap-1">
                      <CheckCircle size={10} />
                      clean
                    </span>
                  )}
                  {repo.ahead > 0 && (
                    <span className="text-[var(--bw-accent)]">
                      ↑{repo.ahead}
                    </span>
                  )}
                  {repo.behind > 0 && (
                    <span className="text-[var(--bw-muted)]">
                      ↓{repo.behind}
                    </span>
                  )}
                </div>

                {/* Row 2: actions */}
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {repo.isDirty && (
                    <>
                      <button
                        type="button"
                        onClick={() => runAction(repo.path, "stage")}
                        disabled={isLoading || disabled}
                        className="flex items-center gap-1 px-2 py-0.5 border border-[var(--bw-border)] hover:border-[var(--bw-accent)] hover:text-[var(--bw-accent)] disabled:opacity-50 transition-colors"
                      >
                        {currentAction === "stage" ? (
                          <Loader2 size={10} className="animate-spin" />
                        ) : (
                          <CheckCircle size={10} />
                        )}
                        Stage all
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setShowCommitInput((p) => ({
                            ...p,
                            [repo.path]: !p[repo.path],
                          }))
                        }
                        disabled={isLoading || disabled}
                        className="flex items-center gap-1 px-2 py-0.5 border border-[var(--bw-border)] hover:border-[var(--bw-accent)] hover:text-[var(--bw-accent)] disabled:opacity-50 transition-colors"
                      >
                        <GitCommit size={10} />
                        Commit
                      </button>
                    </>
                  )}
                  {repo.ahead > 0 && (
                    <button
                      type="button"
                      onClick={() => runAction(repo.path, "push")}
                      disabled={isLoading || disabled}
                      className="flex items-center gap-1 px-2 py-0.5 border border-[var(--bw-border)] hover:border-[var(--bw-accent)] hover:text-[var(--bw-accent)] disabled:opacity-50 transition-colors"
                    >
                      {currentAction === "push" ? (
                        <Loader2 size={10} className="animate-spin" />
                      ) : (
                        <Upload size={10} />
                      )}
                      Push
                    </button>
                  )}
                  {repo.behind > 0 && (
                    <button
                      type="button"
                      onClick={() => runAction(repo.path, "pull")}
                      disabled={isLoading || disabled}
                      className="flex items-center gap-1 px-2 py-0.5 border border-[var(--bw-border)] hover:border-[var(--bw-ok,#7fe)] hover:text-[var(--bw-ok,#7fe)] disabled:opacity-50 transition-colors"
                    >
                      {currentAction === "pull" ? (
                        <Loader2 size={10} className="animate-spin" />
                      ) : (
                        <Download size={10} />
                      )}
                      Pull ↓{repo.behind}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => runAction(repo.path, "fetch")}
                    disabled={isLoading || disabled}
                    className="flex items-center gap-1 px-2 py-0.5 border border-[var(--bw-border)] hover:border-[var(--bw-muted)] hover:text-[var(--bw-muted)] disabled:opacity-50 transition-colors"
                    title="Fetch from remote (updates behind/ahead counts)"
                  >
                    {currentAction === "fetch" ? (
                      <Loader2 size={10} className="animate-spin" />
                    ) : (
                      <RefreshCw size={10} />
                    )}
                    Fetch
                  </button>
                </div>

                {/* Commit message input */}
                {showInput && (
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="text"
                      placeholder="Commit message…"
                      value={msg}
                      onChange={(e) =>
                        setCommitMsg((p) => ({
                          ...p,
                          [repo.path]: e.target.value,
                        }))
                      }
                      disabled={isLoading || disabled}
                      className="flex-1 px-2 py-1 bg-[var(--bw-bg)] border border-[var(--bw-border)] text-[var(--bw-fg)] placeholder:text-[var(--bw-muted)] focus:outline-none focus:border-[var(--bw-accent)]"
                    />
                    <button
                      type="button"
                      onClick={() => runAction(repo.path, "commit", msg)}
                      disabled={isLoading || disabled || !msg.trim()}
                      className="px-2 py-1 border border-[var(--bw-accent)] text-[var(--bw-accent)] hover:bg-[var(--bw-accent)] hover:text-[var(--bw-bg)] disabled:opacity-50 transition-colors"
                    >
                      {currentAction === "commit" ? (
                        <Loader2 size={10} className="animate-spin" />
                      ) : (
                        "OK"
                      )}
                    </button>
                  </div>
                )}

                {/* Error */}
                {error && (
                  <div className="mt-1 text-[var(--bw-warn,#f77)] flex items-center gap-1">
                    <AlertCircle size={10} />
                    {error}
                  </div>
                )}

                {/* Repo-level error from fetch */}
                {repo.error && (
                  <div className="mt-1 text-[var(--bw-muted)]">
                    ⚠ {repo.error}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
